import buttonStyles from "@components/ui-components/Buttons/Button/Button.module.css";
import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { PropertyData } from "../../../to-be-removed/LocalData/Property/PropertyData";
import { UnitData } from "../../../to-be-removed/LocalData/Property/Units/UnitData";
import { SelectInputData, SelectInputField } from "../SelectInputField/SelectInputField";
import { Text } from "../Text/Text";
import styles from "./TenantSelector.module.css";

interface TenantSelectorProps {
  description?: string;
  properties: PropertyData[];
  units?: false | UnitData[];
  isMulti: boolean;
  placeholder?: string;
  width?: string;
  changedSelectedTenants: (tenants: UnitData[]) => void;
  className?: string;
  defaultSelected?: UnitData[] | UnitData | "notSpecified";
  defaultMenuIsOpen?: boolean;
  backgroundColor?: string;
  menuPortalTarget?: HTMLElement | "popup" | "insideModal";
  isClearable?: boolean;
  disabled?: boolean;
  dataCy?: string;
}

export const TenantSelector = (props: TenantSelectorProps) => {
  const [selectedUnits, setSelectedUnits] = React.useState<UnitData[]>([]);
  const [showOnlyActiveTenants, setShowOnlyActiveTenants] = React.useState<boolean>(true);
  const [, setLastUpdate] = useState(new Date());

  let tenantOptions = getTenantOptions();
  const inactiveTenantOptions = getTenantOptions().filter(
    (tenant) =>
      (tenant.contractStartDate?.value && tenant.contractStartDate.value > new Date()) ||
      (tenant.contractEndDate?.value && tenant.contractEndDate.value < new Date())
  );
  if (showOnlyActiveTenants) tenantOptions = tenantOptions.filter((tenant) => !inactiveTenantOptions.includes(tenant));

  useEffect(() => {
    if (props.defaultSelected instanceof UnitData) {
      setSelectedUnits([props.defaultSelected]);
    } else if (Array.isArray(props.defaultSelected)) {
      setSelectedUnits(props.defaultSelected);
    }
  }, [props.defaultSelected]);

  useEffect(() => {
    for (let unit of selectedUnits) {
      if (!unit.loaded) {
        unit.loadWithCompletionHandler(() => setLastUpdate(new Date()));
      }
    }
  }, [selectedUnits]);

  useEffect(() => {
    let newUnits: UnitData[] = [];
    let hasUpdated = false;
    selectedUnits.forEach((unit) => {
      if (props.properties.length === 0 || (unit.parent instanceof PropertyData && props.properties.includes(unit.parent))) newUnits.push(unit);
      else {
        hasUpdated = true;
      }
    });
    if (hasUpdated) {
      setSelectedUnits(newUnits);
      props.changedSelectedTenants(newUnits);
    }

    for (const property of props.properties) {
      if (!property.unitsLoaded) {
        property.loadUnits(() => {
          setLastUpdate(new Date());
          loadUnitInfoFields();
        });
      }
    }
    loadUnitInfoFields();
  }, [props.properties]);

  const loadUnitInfoFields = () => {
    const units = getTenantOptions();
    const promises: Promise<any>[] = [];
    for (const unit of units) {
      if (!unit.infoFieldsLoaded) {
        promises.push(new Promise((r) => unit.loadInfoFields(() => r(unit))));
      }
    }
    Promise.all(promises).finally(() => {
      setLastUpdate(new Date());
    });
  };

  function getTenantOptions() {
    let allOptionsArray: UnitData[] = [];
    if (props.units) {
      for (let unit of props.units) {
        if (!unit.deleted && !unit.archived && !(unit.parent as PropertyData).archived) allOptionsArray.push(unit);
      }
    } else {
      for (let property of props.properties) {
        for (let unit of property.units) {
          if (
            property.checkForLandlordRoles(LocalData.currentUser) ||
            property.checkForEntrepreneurRoles(LocalData.currentUser) ||
            property.members.getData(LocalData.currentUser.id)?.units.find((u) => u.id === unit.id)
          ) {
            if (!unit.deleted && !unit.archived && !(unit.parent as PropertyData).archived) allOptionsArray.push(unit);
          }
        }
      }
    }

    // Check for selected units that are not in the options array
    for (let unit of selectedUnits) {
      if (!allOptionsArray.includes(unit)) allOptionsArray.push(unit);
    }

    allOptionsArray = allOptionsArray.sort((a, b) => a.name.localeCompare(b.name));
    return allOptionsArray;
  }

  const selectAll = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();

    setSelectedUnits(tenantOptions);
    props.changedSelectedTenants(tenantOptions);
  };

  const deselectAll = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    setSelectedUnits([]);
    props.changedSelectedTenants([]);
  };

  function changedSelected(e: any) {
    let tenants: UnitData[] = [];
    if (Array.isArray(e)) {
      for (let data of e) {
        tenants.push(LocalData.getUnitWithId(data.value ?? ""));
      }
    } else if (e && (e as any).value) {
      tenants = [LocalData.getUnitWithId((e as any).value)];
    }
    setSelectedUnits(tenants);
    props.changedSelectedTenants(tenants);
  }

  const shouldShowCustomHeader = inactiveTenantOptions.length > 0 || (props.isMulti && tenantOptions.length > 0);

  return (
    <div
      className={"selector " + (props.className ?? "")}
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: props.width,
      }}
    >
      <Text size="md">{props.description}</Text>

      <SelectInputField
        disabled={props.disabled}
        {...props}
        width={props.width}
        placeholder={props.placeholder}
        options={tenantOptions.map((unit) => new SelectInputData(unit.id, unit.deleted ? t("main.deleted") : unit.name))}
        value={selectedUnits.map((unit) => unit.id)}
        multi={props.isMulti}
        onChange={(e) => changedSelected(e)}
        className={props.className}
        defaultMenuIsOpen={props.defaultMenuIsOpen}
        menuPortalTarget={props.menuPortalTarget}
        backgroundColor={props.backgroundColor}
        isClearable={props.isClearable}
        customHeader={
          shouldShowCustomHeader ? (
            <div className={styles.header}>
              {inactiveTenantOptions.length > 0 && (
                <Checkbox
                  value={showOnlyActiveTenants}
                  onChange={setShowOnlyActiveTenants}
                  description={t("main.showOnlyActiveTenants")}
                  tooltip={t("main.showOnlyActiveTenantsDescription")}
                />
              )}

              {props.isMulti &&
                tenantOptions.length > 0 &&
                (selectedUnits.length !== tenantOptions.length ? (
                  /* For some reason, the react-aria implementation inside <Button /> component makes the dropdown close if using it  */
                  <button onClick={selectAll} className={buttonStyles.button} style={{ width: "100%" }}>
                    + {t("main.selectAll")}
                  </button>
                ) : (
                  <button onClick={deselectAll} className={buttonStyles.button} style={{ width: "100%" }}>
                    - {t("main.deselectAll")}
                  </button>
                ))}
            </div>
          ) : undefined
        }
      />
    </div>
  );
};
